import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMetadata } from '@contexts/org-metadata';
import {
  activeScopeParams,
  useActiveScope,
} from '@features/account-view/active';
import {
  IntegrationTypeIcons,
  getProviderIntegrationName,
} from '../lib/integration';
import { CommonConstant } from '@utility/constants/common';
import { readOrgFromUrl } from '@utility/org-url';
import StorageProviderService from '@utility/services/storage-provider';
import SummaryService from '@utility/services/summary';
import { providerKeys } from '../lib/query-keys';
import type { StorageProviderListItem } from '../types';
import {
  isV2Response,
  type StorageProviderV2ListResponse,
  type StorageProviderV2Row,
} from '../types-v2';
import { pagedFilter, toListParams, usePagedFilter } from './use-paged-filter';

export type UnitsSort = 'asc' | 'desc';

/** EN-4134: the V2 list's tab and filters, sent as status, provider and country. */
export type V2ProvidersFilter = {
  status?: 'active' | 'inactive';
  provider?: string;
  country?: string;
};

type ListParams = Omit<ReturnType<typeof toListParams>, 'sort'> & {
  sort: string | null;
  order?: UnitsSort;
} & V2ProvidersFilter;

type LegacyListResponse = {
  data?: (StorageProviderListItem & { item_inventory?: number })[];
  total_pages?: number;
  total_data?: number;
};

type ProvidersPage = {
  rows: StorageProviderListItem[];
  /** The rows of a `source: 'v2'` answer; null for the legacy one. */
  v2Rows: StorageProviderV2Row[] | null;
  total: number;
  /** EN-4134, V2 only: the rows per tab and what the filters can offer; absent from an older API. */
  counts?: StorageProviderV2ListResponse['counts'];
  facets?: StorageProviderV2ListResponse['facets'];
};

/*
 * Which table the account was on last time, so the page draws it at once
 * instead of a blank page until the first answer (Ely 10-22: the click must be
 * instant). Unknown, it draws the V2 table, the one accounts are moving to.
 */
const VERSION_KEY = 'sk:storage-providers-version';
const versionKey = () => `${VERSION_KEY}:${readOrgFromUrl() ?? ''}`;
const noV2Rows: StorageProviderV2Row[] = [];

function rememberedVersion(): 'v2' | 'legacy' | null {
  try {
    const value = window.localStorage.getItem(versionKey());
    return value === 'v2' || value === 'legacy' ? value : null;
  } catch {
    return null;
  }
}

function rememberVersion(version: 'v2' | 'legacy') {
  try {
    window.localStorage.setItem(versionKey(), version);
  } catch {
    /* Private mode: the next visit draws the V2 table first, as with no memory. */
  }
}

const initialFilter = pagedFilter({
  total_items: CommonConstant.sortDirection.descending,
});
const noRows: StorageProviderListItem[] = [];

async function fetchProviders(
  params: ListParams,
  summary: boolean,
): Promise<ProvidersPage> {
  if (summary) {
    const result: LegacyListResponse =
      await SummaryService.listStorageProviders(params);
    return {
      rows: legacyRows(result),
      v2Rows: null,
      total: result.total_data ?? 0,
    };
  }
  const result: LegacyListResponse | StorageProviderV2ListResponse =
    await StorageProviderService.scm({ ...params, ...activeScopeParams() });
  if (isV2Response<StorageProviderV2ListResponse>(result)) {
    return {
      rows: noRows,
      v2Rows: result.data,
      total: result.total,
      counts: result.counts,
      facets: result.facets,
    };
  }
  return {
    rows: legacyRows(result),
    v2Rows: null,
    total: result.total_data ?? 0,
  };
}

const levelOf = (key: readonly unknown[]) =>
  (key[2] as { scope?: { level?: string } } | undefined)?.scope?.level;

const legacyRows = (result: LegacyListResponse) =>
  (result.data ?? []).map(row => ({
    ...row,
    itemInventory: row.item_inventory ?? row.itemInventory,
  })) as StorageProviderListItem[];

/** The providers table: legacy rows or the V2 ones, by what the API answers. */
export function useProviders() {
  const { isSummaryView } = useMetadata() as { isSummaryView?: boolean };
  const summary = !!isSummaryView;
  const { filter, applied, filterChanged, pageChanged, updateAllFilter } =
    usePagedFilter(initialFilter, toListParams, 'scmStorageProvider');

  /* EN-4396: the level the page settled (account or division); a new level or division filter renders again. */
  const { level } = useActiveScope();
  /* The V2 list sorts on the server (Total Units only); the legacy table keeps its own `sort` filter. */
  const [unitsSort, setUnitsSort] = useState<UnitsSort | undefined>();
  const [v2Filter, setV2Filter] = useState<V2ProvidersFilter>({});
  const params = useMemo<ListParams | null>(() => {
    if (!applied) return null;
    const sorted = unitsSort
      ? { ...applied, sort: 'total_units', order: unitsSort }
      : applied;
    return { ...sorted, ...v2Filter };
  }, [applied, unitsSort, v2Filter]);
  const query = useQuery({
    queryKey: providerKeys.list(params, summary),
    queryFn: () => fetchProviders(params!, summary),
    enabled: !!params,
    /* A return visit draws the rows it saw at once and refreshes them behind. */
    staleTime: 30_000,
    gcTime: 30 * 60_000,
    // A new page or filter keeps the rows shown; a new level never shows the other level's rows.
    placeholderData: (previous, previousQuery) =>
      previousQuery && levelOf(previousQuery.queryKey) === level
        ? keepPreviousData(previous)
        : undefined,
  });

  const page = query.data;
  const shown = useRef<StorageProviderV2Row[] | null>(undefined);
  const shownLevel = useRef(level);
  if (shownLevel.current !== level) {
    shownLevel.current = level;
    shown.current = undefined;
  }
  if (page) shown.current = page.v2Rows;
  const answered = page ? (page.v2Rows ? 'v2' : 'legacy') : null;
  useEffect(() => {
    if (answered) rememberVersion(answered);
  }, [answered]);
  return {
    data: page?.rows ?? noRows,
    loading: !applied || query.isFetching,
    /* No rows to show yet: the table draws its placeholder rows. A refresh
       behind rows already shown keeps them; a failed first load shows its error. */
    pending: !page && !query.isError,
    error: query.error?.message ?? '',
    filter,
    filterChanged,
    pageChanged,
    updateAllFilter,
    unitsSort,
    unitsSortChanged: (next: UnitsSort | undefined) => {
      setUnitsSort(next);
      pageChanged(1);
    },
    totalData: page?.total ?? 0,
    v2Filter,
    v2FilterChanged: (next: V2ProvidersFilter) => {
      setV2Filter(next);
      pageChanged(1);
    },
    counts: page?.counts,
    facets: page?.facets,
    refresh: async () => {
      await query.refetch();
    },
    /*
     * Before the first answer, the version the account was on last time (V2
     * when unknown), empty, so the page draws its table at once. A failed
     * first load shows the legacy page and its error; a later one keeps the
     * version shown.
     */
    v2Rows:
      shown.current !== undefined
        ? shown.current
        : query.isError || rememberedVersion() === 'legacy'
          ? null
          : noV2Rows,
  };
}

/** The provider types the Type filter offers. */
/** `enabled` false on a V2 account: the types are a legacy (Mongo) distinct, and the V2 list carries its own facets. */
/* One empty list for every render: a fresh [] each time made the list page's filters, and so its URL sync, run again
   on every render (a navigate loop) while the types are not loaded, as on V2 where they never are. */
const NO_PROVIDER_TYPES: never[] = [];

export function useProviderTypes(enabled = true) {
  const query = useQuery({
    queryKey: providerKeys.types(),
    enabled,
    queryFn: async () =>
      ((await StorageProviderService.providerTypes())?.data ?? []) as string[],
    staleTime: Number.POSITIVE_INFINITY,
    select: types =>
      types.map(type => ({
        text: getProviderIntegrationName(type),
        value: type,
        image: {
          src: IntegrationTypeIcons[type] || IntegrationTypeIcons.default,
        },
      })),
  });
  return { data: query.data ?? NO_PROVIDER_TYPES, loading: query.isLoading };
}
